import React, { useState, useEffect } from 'react';
import { 
  ArrowPathIcon,
  ChartBarIcon,
  ClockIcon,
  UserGroupIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { TaskAutomationRule } from '../types/Automation';
import { useBranch } from '../contexts/BranchContext';
import PermissionGate from './PermissionGate';
import api from '../services/api';

interface RotationStats {
  total_executions: number;
  unique_assignees: number;
  unique_supervisors: number;
  first_execution: string;
  last_execution: string;
  avg_rotation_index: number;
}

interface RotationHistoryItem {
  id: number;
  automation_rule_id: number;
  organization_id: number;
  branch_id: number;
  lead_id: number;
  assigned_assignee_id: number;
  assigned_supervisor_id: number;
  rotation_index: number;
  execution_date: string;
  execution_status: string;
  lead_first_name: string;
  lead_last_name: string;
  lead_email: string;
  assignee_first_name: string;
  assignee_last_name: string;
  supervisor_first_name: string;
  supervisor_last_name: string;
}

const RotationTracker: React.FC = () => {
  const { currentBranch, isViewingAllBranches } = useBranch();
  const [rules, setRules] = useState<TaskAutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRule, setSelectedRule] = useState<TaskAutomationRule | null>(null);
  const [rotationHistory, setRotationHistory] = useState<RotationHistoryItem[]>([]);
  const [rotationStats, setRotationStats] = useState<RotationStats | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Load rotation overview
  const loadRotationOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/automation/rotation-overview');
      setRules(response.data.data || []);
    } catch (err: any) {
      console.error('Error loading rotation overview:', err);
      
      // Handle different types of errors
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view rotation data.');
      } else {
        setError(err.message || 'Failed to load rotation overview');
      }
      
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  // Load rotation history for a specific rule
  const loadRotationHistory = async (ruleId: number) => {
    try {
      setHistoryLoading(true);
      
      const response = await api.get(`/automation/task-rules/${ruleId}/rotation-history`);
      setRotationHistory(response.data.data || []);
    } catch (err: any) {
      console.error('Error loading rotation history:', err);
      setRotationHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load rotation stats for a specific rule
  const loadRotationStats = async (ruleId: number) => {
    try {
      const response = await api.get(`/automation/task-rules/${ruleId}/rotation-stats`);
      setRotationStats(response.data.data);
    } catch (err: any) {
      console.error('Error loading rotation stats:', err);
      setRotationStats(null);
    }
  };

  // Reset rotation counter
  const resetRotation = async (ruleId: number) => {
    try {
      await api.post(`/automation/task-rules/${ruleId}/reset-rotation`);
      
      // Reload the overview to reflect changes
      await loadRotationOverview();
      
      // If this rule is currently selected, reload its stats
      if (selectedRule && selectedRule.id === ruleId) {
        await loadRotationStats(ruleId);
      }
    } catch (err: any) {
      console.error('Error resetting rotation:', err);
      alert('Failed to reset rotation: ' + err.message);
    }
  };

  // Handle rule selection
  const handleRuleSelect = async (rule: TaskAutomationRule) => {
    setSelectedRule(rule);
    setShowHistory(true);
    await Promise.all([
      loadRotationHistory(rule.id),
      loadRotationStats(rule.id)
    ]);
  };

  useEffect(() => {
    loadRotationOverview();
  }, [currentBranch, isViewingAllBranches]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading rotation data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Rotation Tracker</h2>
          <p className="text-sm text-gray-600">
            Monitor and manage rotation assignments for automation rules
          </p>
        </div>
        <button
          onClick={loadRotationOverview}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rotation Overview */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Rotation Overview</h3>
          
          {rules.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <ArrowPathIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No rotation rules</h3>
              <p className="mt-1 text-sm text-gray-500">
                Create automation rules with "Divide" assignment mode to see rotation tracking.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div key={rule.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium text-gray-900">{rule.taskName}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          rule.assignmentMode === 'assign_all' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {rule.assignmentMode === 'assign_all' ? 'Assign All' : 'Divide'}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <div><span className="font-medium">Trigger:</span> {rule.stageName}</div>
                        <div><span className="font-medium">Assignees:</span> {
                          rule.assigneeIds && rule.assigneeIds.length > 0 
                            ? `${rule.assigneeIds.length} selected` 
                            : 'Not set'
                        }</div>
                        <div><span className="font-medium">Supervisors:</span> {
                          rule.supervisorIds && rule.supervisorIds.length > 0 
                            ? `${rule.supervisorIds.length} selected` 
                            : 'Not set'
                        }</div>
                        
                        {rule.assignmentMode === 'divide' && (
                          <>
                            <div><span className="font-medium">Interval:</span> Every {rule.rotationInterval} change(s)</div>
                            <div><span className="font-medium">Total Rotations:</span> {rule.totalRotations || 0}</div>
                            <div><span className="font-medium">Last Rotation:</span> {
                              rule.lastRotationDate 
                                ? new Date(rule.lastRotationDate).toLocaleDateString()
                                : 'Never'
                            }</div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleRuleSelect(rule)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <EyeIcon className="h-3 w-3 mr-1" />
                        View History
                      </button>
                      
                      {rule.assignmentMode === 'divide' && (
                        <PermissionGate module="automation" permission="edit">
                          <button
                            onClick={() => resetRotation(rule.id)}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <ArrowPathIcon className="h-3 w-3 mr-1" />
                            Reset
                          </button>
                        </PermissionGate>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rotation Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Rotation Details</h3>
          
          {!selectedRule ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Select a rule</h3>
              <p className="mt-1 text-sm text-gray-500">
                Choose a rule from the overview to view its rotation details and history.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Current Rule Info */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{selectedRule.taskName}</h4>
                
                {rotationStats && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-blue-50 p-3 rounded">
                      <div className="font-medium text-blue-900">Total Executions</div>
                      <div className="text-2xl font-bold text-blue-600">{rotationStats.total_executions}</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                      <div className="font-medium text-green-900">Unique Assignees</div>
                      <div className="text-2xl font-bold text-green-600">{rotationStats.unique_assignees}</div>
                    </div>
                    <div className="bg-purple-50 p-3 rounded">
                      <div className="font-medium text-purple-900">Unique Supervisors</div>
                      <div className="text-2xl font-bold text-purple-600">{rotationStats.unique_supervisors}</div>
                    </div>
                    <div className="bg-orange-50 p-3 rounded">
                      <div className="font-medium text-orange-900">Avg Rotation Index</div>
                      <div className="text-2xl font-bold text-orange-600">
                        {rotationStats.avg_rotation_index && typeof rotationStats.avg_rotation_index === 'number' 
                          ? rotationStats.avg_rotation_index.toFixed(1) 
                          : '0.0'}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Rotation History */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Recent Rotation History</h4>
                  {historyLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  )}
                </div>
                
                {rotationHistory.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <ClockIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm">No rotation history available</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {rotationHistory.slice(0, 10).map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {item.lead_first_name} {item.lead_last_name}
                          </div>
                          <div className="text-gray-600">
                            Assigned to: {item.assignee_first_name} {item.assignee_last_name}
                            {item.supervisor_first_name && (
                              <span> | Supervised by: {item.supervisor_first_name} {item.supervisor_last_name}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-gray-500">
                          <div className="text-xs">
                            Rotation #{item.rotation_index}
                          </div>
                          <div className="text-xs">
                            {new Date(item.execution_date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RotationTracker;
